/*
 *  Copyright (c) Qwoach LLC 2023. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 8/12/2023
 */
import {classToClass} from "@qwoach/class-transformer-wrapper";
import BaseObject from "@shared/BaseObject.js";
import AuthManager from "@/core/js/backend/AuthManager.js";
import QwoachDataManager from "@/core/js/backend/QwoachDataManager.js";
import PortalRef from "@shared/Profile/PortalRef.js";
import {RoleEnum} from "@shared/Permissions/RoleEnum.js";
import {getGrantedClearanceLevelArray} from "@shared/Profile/ClearanceLevelEnum.js";
import {getClearanceAllowKey} from "@/utils/helpers.js";
import QwoachFile from "@shared/Drive/QwoachFile";

export default class PortalManager extends BaseObject {

    authManager: AuthManager
    dataManager: QwoachDataManager

    portalRefCache = {}

    constructor(authManager) {
        super();
        this.authManager = authManager;
        this.dataManager = authManager.dataManager;
    }


	decorateDataFilterForNonOwner(dataFilter, options){
		let userProfile = this.authManager.getUserProfile()
		if(!userProfile.isOwner() || options.onBehalfOfMember){
			let requiresClearance = options.requiresClearance || false
			let permissions = options.permissions || []
			console.log("PortalManager.decorateDataFilterForNonOwner: ", ...arguments)
			let accessRuleItem
			if(options.onBehalfOfMember){
				accessRuleItem = options.onBehalfOfMember.accessRuleItem
			} else {
				accessRuleItem = userProfile.getCurrentOwnerAccessRuleItem()
			}

			console.log("PortalManager.decorateDataFilterForNonOwner: accessRuleItem = ", accessRuleItem)
			if(!accessRuleItem){
				return dataFilter
			}
			let userId = accessRuleItem.myIdForOwner
			let clearanceLevel = accessRuleItem.clearanceLevel
			let grantedClearanceLevelArray = getGrantedClearanceLevelArray(clearanceLevel)


			//user id can be either 'userid' or 'direct_userid' depending on how a resource was shared
			let shareWithIdsUserId = this.getShareWithIdsUserId(userId, dataFilter)

			let userPermissions = accessRuleItem.getPermissions()
			if(userProfile.isClient()){
				userPermissions = [...userPermissions, "scheduler_scope_all"]
			}
			let hasPermissions = permissions.qwIncludesAny(userPermissions)
			console.log("PortalManager.decorateDataFilterForNonOwner: userPermissions = ", userPermissions)
			console.log("PortalManager.decorateDataFilterForNonOwner: permissions = ", permissions)

			let dataFilter2 = classToClass(dataFilter)
			dataFilter2.filterOptions.reset()
			if(!requiresClearance){
				dataFilter = dataFilter2
					.whereFilter(dataFilter.filterOptions)
					.whereIf("sharedWithIds", "array-contains", shareWithIdsUserId, !hasPermissions)
			} else {
				dataFilter = dataFilter2
					.andStart()
						.whereFilter(dataFilter.filterOptions)
						.orStart()
							.where("sharedWithIds", "array-contains", getClearanceAllowKey(userId))
							.andStart()
								.whereIf("sharedWithIds", "array-contains", shareWithIdsUserId, !hasPermissions)
								.orStart()
									.where("createdById", "==", userId)
									.where("clearanceLevel", "in", grantedClearanceLevelArray)
								.orEnd()
							.andEnd()
						.orEnd()
					.andEnd()
			}

			console.log("PortalManager.decorateDataFilterForNonOwner: dataFilter = ", dataFilter)
		}
		return dataFilter
	}

	getShareWithIdsUserId(userId, dataFilter){

		let shareWithIdsUserId = userId

		let directShareUserId = QwoachFile.asDirectSharedId(userId)

		let shareWithIdsFilter = dataFilter.filterOptions.filterOptions.find(option => option.field === "sharedWithIds")
		if(shareWithIdsFilter){
			let {value} = shareWithIdsFilter
			if(!Array.isArray(value)){
				value = [value]
			}
			if(value.includes(directShareUserId)){
				shareWithIdsUserId = directShareUserId
			}
		}

		return shareWithIdsUserId

	}


	async getCurrenUserPortalRefsArray(includeCurrentPortal = true) {
		let userProfile = this.authManager.getUserProfile()
		let portalRefs = await this.getPortalRefsArray(userProfile)
		if(portalRefs.length === 1){
			includeCurrentPortal = false
		}
		console.log("PortalManager.getCurrenUserPortalRefsArray: ", portalRefs)
		if(!includeCurrentPortal && userProfile.currentPortalRef){
			let portalRef = userProfile.currentPortalRef
			portalRefs = portalRefs.filter(ref => ref.ownerId !== portalRef.ownerId || ref.role !== portalRef.role)
		}
		return portalRefs
	}


	async getPortalRefsArray(userProfile){
        let key = this.getCacheKey(userProfile)
        if(!this.portalRefCache[key]){
            this.portalRefCache[key] = await this._getPortalRefsArrayInternal(userProfile)
        }
		console.log("PortalManager.getPortalRefsArray: ", this.portalRefCache)
        return this.portalRefCache[key]
    }

    getCacheKey(userProfile){
		// console.trace("PortalManager.getCacheKey: ", userProfile)
        let key = ""
        for(let ownerId of Object.keys(userProfile.accessRules)) {
            key += ownerId
            let accessRuleItem = userProfile.accessRules[ownerId];
            for (let role of accessRuleItem.roles) {
                key += `_${role}`
            }
        }
        return key
    }

    async _getPortalRefsArrayInternal(userProfile){
        let res = []
        let ownerProfile = this.authManager.getOwnerProfile()
		console.log("PortalManager._getPortalRefsArrayInternal: ownerProfile = ", ownerProfile)
        for(let ownerId of Object.keys(userProfile.accessRules)){
			console.log("PortalManager._getPortalRefsArrayInternal: ownerId = ", ownerId)
            let accessRuleItem = userProfile.accessRules[ownerId];
			console.log("PortalManager._getPortalRefsArrayInternal: accessRuleItem = ", accessRuleItem)
            let user
            if(ownerProfile && ownerProfile.id === ownerId){
                user = ownerProfile
            } else {
                user = await this.dataManager.getUserProfile(ownerId)
            }
            for(let role of accessRuleItem.roles){
                let portalRef = new PortalRef()
                portalRef.role = role;
                portalRef.userId = accessRuleItem.myIdForOwner
                portalRef.userAuthId = userProfile.id
                portalRef.ownerId = ownerId
                if(user){
                    portalRef.ownerTitle = user.name
                }
                res.push(portalRef)
            }
        }

        // For clients, we only show client portal by default, unless they've opted in for coach profile.
        // For rest, we always include self portal
        let dontAddSelfPortal = res.length === 1 && res[0].role === "client" && !userProfile.clientOptedInAsCoach
        if(!dontAddSelfPortal){
            res.push(this.createSelfPortalRef(userProfile))
        }
        return res
    }

    createSelfPortalRef(userProfile){
        let portalRef = new PortalRef()
        portalRef.role = userProfile.role || RoleEnum.COACH;
        portalRef.userId = userProfile.id
        portalRef.userAuthId = userProfile.id
        portalRef.ownerId = userProfile.id
        portalRef.ownerTitle = "My Profile"
        return portalRef
    }

    async setDefaultPortal(userProfile){
		console.log("PortalManager.setDefaultPortal: userProfile = ", classToClass(userProfile))
        let portalRefs = await this.getPortalRefsArray(userProfile)
		console.log("PortalManager.setDefaultPortal: portalRefs = ", portalRefs)
		let index = portalRefs.findIndex(ref => ref.ownerId === userProfile.getCoachId())
		let shouldSetPortal = index < 0 || !userProfile.isPortalSet()
		console.log("PortalManager.setDefaultPortal: index = ", index)
		// can't change the order below:
		if(shouldSetPortal){
			// 1. set default portalRef
			userProfile.setPortalRef(portalRefs[0])
		}
		console.log("PortalManager.setDefaultPortal: userProfile = ", userProfile)
		// let a = 0
		// if (a === a)throw Error("stop")

		// 2. set current owner profile - this set's the base ownerId for dataManager internally
		await this.authManager.setCurrentCoachProfile(userProfile)
		if(shouldSetPortal){
			// 3. save userProfile
			await this.dataManager.updateUserProfile(userProfile)
		}
    }

}
